import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import AsoebiModal from "@src/components/AsoebiModal";
import Dialog from "src/components/Dialog";

interface Props {
  eventUuid: string | undefined;
}

const AsoEbi = ({ eventUuid }: Props) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const borderVariants = {
    hidden: {
      opacity: 0,
      scaleX: 0,
    },
    visible: {
      opacity: 1,
      scaleX: 1,
      transition: {
        duration: 1.2,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "backOut",
        delay: 1.2,
      },
    },
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1,
      },
    },
  };

  const [openAsoebi, setOpenAsoebi] = useState(false);

  return (
    <motion.div
      className="min-h-[50vh] lg:min-h-[40vh] bg-white flex flex-col justify-center py-[4rem] md:py-[5rem] md:pt-[6rem]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.div
        ref={ref}
        className="min-h-[20rem] md:min-h-[35rem] flex flex-col justify-center items-center relative px-8 py-[4rem] bg-white"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {/* Top border frames */}
        <motion.div
          className="flex justify-center h-3 w-[90%] relative px-8 overflow-hidden mb-3"
          style={{
            backgroundImage: `url('/images/Frame.svg')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
          variants={borderVariants}
        />
        <motion.div
          className="flex justify-center h-3 w-[90%] relative px-8 overflow-hidden mb-10"
          style={{
            backgroundImage: `url('/images/Frame.svg')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
          variants={borderVariants}
        />

        {/* Content */}
        <motion.div
          className="flex flex-col justify-center items-center w-full lg:w-3/4 relative z-10 my-4 lg:my-10"
          variants={containerVariants}
        >
          <motion.h3
            className="font-gill font-light tracking-wide text-4xl uppercase text-center text-black mb-4 sm:mb-10"
            variants={itemVariants}
            whileHover={{
              scale: 1.02,
              color: "#8B7A85",
              transition: { duration: 0.3 },
            }}
          >
            Our Asoebi
          </motion.h3>

          <motion.h5
            className="font-gill font-normal text-center text-lg lg:text-2xl text-black mb-8"
            variants={itemVariants}
          >
            See the beautiful asoebi for our big day!
          </motion.h5>

          <Dialog
            trigger={
              <motion.div
                className="flex flex-col md:flex-row gap-8 justify-center items-center"
                variants={itemVariants}
              >
                <motion.button
                  className="px-8 py-3 rounded transition duration-300 border-black text-black text-xl font-gill uppercase hover:bg-black hover:text-white relative overflow-hidden"
                  type="button"
                  variants={buttonVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  whileHover="hover"
                  whileTap="tap"
                >
                  {/* Animated border glow */}
                  <motion.div
                    className="absolute inset-0 border-2 border-black rounded"
                    animate={{
                      boxShadow: [
                        "0 0 0px rgba(96, 83, 90, 0.3)",
                        "0 0 15px rgba(96, 83, 90, 0.6)",
                        "0 0 0px rgba(96, 83, 90, 0.3)",
                      ],
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                  />

                  <p className="relative z-10">See Asoebi</p>
                </motion.button>
              </motion.div>
            }
            open={openAsoebi}
            onOpenChange={setOpenAsoebi}
          >
            <AsoebiModal eventUuid={eventUuid} setOpen={setOpenAsoebi} />
          </Dialog>
        </motion.div>

        {/* Bottom border frames */}
        <motion.div
          className="flex justify-center h-3 w-[90%] relative px-8 overflow-hidden mt-10"
          style={{
            backgroundImage: `url('/images/Frame.svg')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
          variants={borderVariants}
        />
        <motion.div
          className="flex justify-center h-3 w-[90%] relative px-8 overflow-hidden mt-3"
          style={{
            backgroundImage: `url('/images/Frame.svg')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
          variants={borderVariants}
        />

        {/* Floating decorative elements */}
        <motion.div
          className="absolute top-10 left-10 w-2 h-2 bg-black/30 rounded-full"
          animate={{
            y: [0, -10, 0],
            opacity: [0.3, 0.7, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
          }}
        />

        <motion.div
          className="absolute bottom-20 right-20 w-3 h-3 bg-black/20 rounded-full"
          animate={{
            y: [0, -15, 0],
            x: [0, 5, 0],
            opacity: [0.2, 0.6, 0.2],
          }}
          transition={{
            duration: 4,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
            delay: 1,
          }}
        />
      </motion.div>
    </motion.div>
  );
};

export default AsoEbi;
